import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
  FormTreeData,
  QuestionData,
  NavigationSection,
  FormData,
  FormFieldValue,
} from '@/features/submission_forms/types';
import { createFieldSchema } from '@/features/submission_forms/fieldDefinitions';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

export interface UseFormSchemaResult {
  form: UseFormReturn<FormData>;
  navigationSections: NavigationSection[];
}

const buildNavigationSections = (
  formTree: FormTreeData[]
): NavigationSection[] => {
  return formTree.map(node => ({
    id: node.id,
    label: node.label,
    status: 'not_started',
  }));
};

export const buildQuestionData = (formTree: FormTreeData[]): QuestionData[] => {
  const questionData: QuestionData[] = [];

  const traverse = (nodes: FormTreeData[]) => {
    nodes.forEach(node => {
      node.questions.forEach(question => {
        questionData.push(question);
      });
      // Also traverse children
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };

  // recursively traverse the form tree and collect questions
  traverse(formTree);
  return questionData;
};

const buildFormConfig = (
  questionData: QuestionData[],
  savedAnswers?: Record<string, FormFieldValue>
) => {
  // If there are no questions, return empty config
  if (questionData.length === 0) {
    return {
      schema: z.object({}),
      defaultValues: {},
    };
  }

  const schemaFields: Record<string, z.ZodTypeAny> = {};
  const defaults: Record<string, unknown> = {};

  questionData.forEach(question => {
    const fieldResult = createFieldSchema({
      ...question,
      required: question.required && question.depends_on === null, // If the question has a dependency, it is not required as the backend will handle this validation
    });
    schemaFields[question.slug] = fieldResult.schema;

    // Use saved answer if available, otherwise use default
    const savedValue = savedAnswers?.[question.slug];
    defaults[question.slug] =
      savedValue !== undefined
        ? fieldResult.normalizeSavedValue(savedValue)
        : fieldResult.defaultValue;
  });

  const schema = z.object(schemaFields);

  return {
    schema,
    defaultValues: defaults,
  };
};

/**
 * Custom hook to build form schema and default values from form tree
 */
export const useFormTree = (
  formTree: FormTreeData[],
  savedAnswers?: Record<string, FormFieldValue>
): UseFormSchemaResult => {
  // Store initial saved answers to prevent form resets on subsequent updates
  // useState (not useRef) avoids reading .current during render
  const [initialSavedAnswers] = useState(savedAnswers);

  // Memoize the form configuration to avoid rebuilding unless the form tree changes
  // Uses initial saved answers only to prevent form resets during autosave
  const formConfig = useMemo(() => {
    // Single walk through to collect questions
    const questionData: QuestionData[] = buildQuestionData(formTree);

    // Build schema and defaults configuration using INITIAL saved answers
    const config = buildFormConfig(questionData, initialSavedAnswers);

    return {
      schema: config.schema,
      defaultValues: config.defaultValues,
    };
  }, [formTree, initialSavedAnswers]); // Only rebuild when formTree changes, not when savedAnswers update

  // Setup form using the memoized configuration
  const form = useForm({
    resolver: zodResolver(formConfig.schema),
    defaultValues: formConfig.defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // Build navigation sections with current form values
  const navigationSections = buildNavigationSections(formTree);

  return {
    form,
    navigationSections,
  };
};
